import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  featureFlagQueries,
  useCreateFeatureFlag,
  useDeleteFeatureFlag,
  useUpdateFeatureFlag,
} from 'hooks/backend/featureFlags';
import { useModal } from 'hooks/modals';
import { showErrorMessage, showSuccessMessage } from 'util/notifications';

import Card from 'components/common/Card';
import ErrorDisplay from 'components/common/ErrorDisplay';
import Checkbox from 'components/common/forms/Checkbox';
import Input from 'components/common/forms/Input';

import { DeveloperBackButton } from '../DeveloperBackButton';
import { DeveloperConfirmationModal } from '../DeveloperConfirmationModal';
import { FeatureFlagsGrid } from './FeatureFlagsGrid';
import { FeatureFlagsPlaceholderRows } from './FeatureFlagsPlaceholderRows';
import { FeatureFlagRow } from './FeatureFlagRow';
import { FeatureFlag } from 'types/domainModels';
import Textarea from 'components/common/forms/Textarea';
import Button from 'components/common/forms/Button';

export const FeatureFlags = ({
  setActiveTool,
}: {
  setActiveTool(arg: null): void;
}) => {
  const {
    data: featureFlags = [],
    error,
    isError,
    isLoading,
  } = useQuery(featureFlagQueries.list());
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [flagToDelete, setFlagToDelete] = useState<FeatureFlag | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isEnabledAdmin, setIsEnabledAdmin] = useState(false);
  const [isEnabledDev, setIsEnabledDev] = useState(false);
  const [customPropertiesText, setCustomPropertiesText] = useState('');
  const [customPropertiesError, setCustomPropertiesError] = useState<
    string | null
  >(null);

  const { isOpen, onCloseModal, setIsOpen } = useModal();
  const {
    isOpen: isDeleteModalOpen,
    onCloseModal: onCloseDeleteModal,
    setIsOpen: setIsDeleteModalOpen,
  } = useModal();

  const { isPending: isUpdatingFlag, mutate: updateFlag } =
    useUpdateFeatureFlag({
      onError: (err) => showErrorMessage({ err }),
      onSuccess: (data) => {
        showSuccessMessage(`Updated feature flag "${data.name}".`);
        onCloseModal();
      },
    });

  const { isPending: isCreatingFlag, mutate: createFlag } =
    useCreateFeatureFlag({
      onError: (err) => showErrorMessage({ err }),
      onSuccess: (data) => {
        showSuccessMessage(`Created feature flag "${data.name}".`);
        onCloseModal();
      },
    });

  const { isPending: isDeletingFlag, mutate: deleteFlag } =
    useDeleteFeatureFlag({
      onError: (err) => showErrorMessage({ err }),
      onSuccess: () => {
        showSuccessMessage(`Deleted feature flag "${flagToDelete?.name}".`);
        onCloseDeleteModal();
        setFlagToDelete(null);
      },
    });

  const handleFlagOpenModal = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setName(flag.name);
    setDescription(flag.description);
    setIsEnabled(flag.isEnabled);
    setIsEnabledAdmin(flag.isEnabledAdmin);
    setIsEnabledDev(flag.isEnabledDev);
    setCustomPropertiesText(
      flag.customProperties
        ? JSON.stringify(flag.customProperties, null, 2)
        : '',
    );
    setCustomPropertiesError(null);
    setIsOpen(true);
  };

  const handleAddOpenModal = () => {
    setSelectedFlag(null);
    setName('');
    setDescription('');
    setIsEnabled(false);
    setIsEnabledAdmin(false);
    setIsEnabledDev(false);
    setCustomPropertiesText('');
    setCustomPropertiesError(null);
    setIsOpen(true);
  };

  const handleFlagDeleteOpenModal = (flag: FeatureFlag) => {
    setFlagToDelete(flag);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!flagToDelete) {
      return;
    }

    deleteFlag({ featureFlagId: flagToDelete.id });
  };

  const handleCustomPropertiesChange = (value: string) => {
    setCustomPropertiesText(value);

    if (!value.trim()) {
      setCustomPropertiesError(null);
      return;
    }

    try {
      JSON.parse(value);
      setCustomPropertiesError(null);
    } catch {
      setCustomPropertiesError('Invalid JSON');
    }
  };

  const handleConfirm = () => {
    let customProperties: unknown = null;
    if (customPropertiesText.trim()) {
      try {
        customProperties = JSON.parse(customPropertiesText);
      } catch {
        setCustomPropertiesError('Invalid JSON');
        return;
      }
    }

    const data = {
      name,
      description,
      isEnabled,
      isEnabledAdmin,
      isEnabledDev,
      customProperties,
    };

    if (selectedFlag) {
      updateFlag({ featureFlagId: selectedFlag.id, data });
    } else {
      createFlag({ data });
    }
  };

  return (
    <div className="space-y-6">
      <DeveloperBackButton setActiveTool={setActiveTool} />

      <div className="flex items-center justify-between px-4">
        <div className="text-lg text-gray-d-700">Feature Flags</div>
        <Button hierarchy="primary" onClick={handleAddOpenModal} size="md">
          Add Flag
        </Button>
      </div>

      <Card>
        <div className="bg-white py-2 border-b border-gray-300 text-gray-600 font-normal">
          <FeatureFlagsGrid
            customProperties="Custom Properties"
            description="Description"
            isEnabled="Enabled"
            isEnabledAdmin="Enabled (Admin)"
            isEnabledDev="Enabled (Dev)"
            name="Name"
          />
        </div>

        <div>
          {isLoading ? (
            <FeatureFlagsPlaceholderRows />
          ) : isError ? (
            <ErrorDisplay
              message={`Failed to load feature flags. ${error.message}`}
            />
          ) : featureFlags.length > 0 ? (
            featureFlags.map((flag) => (
              <FeatureFlagRow
                key={flag.id}
                flag={flag}
                handleFlagDeleteOpenModal={handleFlagDeleteOpenModal}
                handleFlagOpenModal={handleFlagOpenModal}
              />
            ))
          ) : (
            <div className="py-4 text-sm text-gray-600">
              No feature flags found.
            </div>
          )}
        </div>
      </Card>
      {isOpen && (
        <DeveloperConfirmationModal
          confirmLabel={selectedFlag ? 'Save changes' : 'Create flag'}
          isConfirmDisabled={!name.trim() || !!customPropertiesError}
          isConfirming={isUpdatingFlag || isCreatingFlag}
          onCloseModal={onCloseModal}
          onConfirm={handleConfirm}
          rows={[
            {
              label: 'Name',
              value: (
                <Input
                  onChange={(event) => setName(event.target.value)}
                  size="md"
                  value={name}
                />
              ),
            },
            {
              label: 'Description',
              value: (
                <Textarea
                  onChange={(event) => setDescription(event.target.value)}
                  size="md"
                  value={description}
                />
              ),
            },
            {
              label: 'Is Enabled for all users?',
              value: (
                <Checkbox
                  checked={isEnabled}
                  name={`feature-flag-${selectedFlag?.id ?? 'new'}-isEnabled`}
                  onClick={() => setIsEnabled((value) => !value)}
                  tag="div"
                />
              ),
            },
            {
              label: 'Is Enabled for admins?',
              value: (
                <Checkbox
                  checked={isEnabledAdmin}
                  name={`feature-flag-${selectedFlag?.id ?? 'new'}-isEnabledAdmin`}
                  onClick={() => setIsEnabledAdmin((value) => !value)}
                  tag="div"
                />
              ),
            },
            {
              label: 'Is Enabled for devs?',
              value: (
                <Checkbox
                  checked={isEnabledDev}
                  name={`feature-flag-${selectedFlag?.id ?? 'new'}-isEnabledDev`}
                  onClick={() => setIsEnabledDev((value) => !value)}
                  tag="div"
                />
              ),
            },
            {
              label: 'Custom properties: (must be valid JSON)',
              value: (
                <div>
                  <Textarea
                    onChange={(event) =>
                      handleCustomPropertiesChange(event.target.value)
                    }
                    size="md"
                    value={customPropertiesText}
                  />
                  {customPropertiesError && (
                    <div className="mt-1 text-red text-xs">
                      {customPropertiesError}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
          title={selectedFlag ? 'Feature flag details' : 'Add feature flag'}
        />
      )}
      {isDeleteModalOpen && flagToDelete && (
        <DeveloperConfirmationModal
          confirmLabel="Delete"
          isConfirming={isDeletingFlag}
          onCloseModal={onCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          rows={[
            {
              label: `Are you sure you want to delete "${flagToDelete.name}"?`,
              value: '',
            },
            { label: 'This cannot be undone', value: '' },
          ]}
          title="Delete feature flag"
        />
      )}
    </div>
  );
};
